import { useState, useEffect } from "react";
import { AlertCircle, X } from "lucide-react";
import Header from "../../components/common/Header";
import axios from "axios";

const Lightbox = ({ image, title, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
      >
        <X className="w-8 h-8" />
      </button>
      <div
        className="max-w-7xl max-h-full"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={image}
          alt={title}
          className="max-w-full max-h-[90vh] object-contain mx-auto"
        />
        <p className="text-white text-center mt-4 text-lg">{title}</p>
      </div>
    </div>
  );
};

// Individual Image Card Component
const ImageCard = ({ image, title, index, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <div className="relative overflow-hidden h-64">
        <img
          src={image}
          alt={`${title} - Image ${index + 1}`}
          className="w-full h-full object-cover transition-transform duration-500"
          style={{ transform: isHovered ? "scale(1.02)" : "scale(1)" }}
        />
        <div
          className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-300"
          style={{ opacity: isHovered ? 1 : 0 }}
        >
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <p className="text-white text-sm font-semibold">View Image</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Loading Skeleton Component
const ImageCardSkeleton = () => {
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden animate-pulse">
      <div className="w-full h-64 bg-gray-300"></div>
    </div>
  );
};

// Error Component
const ErrorMessage = ({ message, onRetry }) => {
  return (
    <div className="col-span-full flex flex-col items-center justify-center py-20">
      <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 max-w-md text-center">
        <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-red-900 mb-3">
          Error Loading Content
        </h3>
        <p className="text-red-700 mb-6">{message}</p>
        <button
          onClick={onRetry}
          className="px-8 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-all"
        >
          Try Again
        </button>
      </div>
    </div>
  );
};

// Empty State Component
const EmptyState = () => {
  return (
    <div className="col-span-full flex flex-col items-center justify-center py-20">
      <div className="bg-gray-100 rounded-xl p-10 max-w-md text-center">
        <h3 className="text-2xl font-bold text-gray-800 mb-3">
          No Content Available
        </h3>
        <p className="text-gray-600">
          There are no images available at the moment. Please check back later.
        </p>
      </div>
    </div>
  );
};

// Blog Section Component - Groups images by blog
const BlogSection = ({ blog, onImageClick }) => {
  return (
    <div className="mb-16">
      {/* Blog Title - Display once */}
      <h2 className="text-4xl font-extrabold text-gray-900 mb-8 pb-4 border-b-2 border-gray-200 heading">
        {blog.title}
      </h2>

      {/* Images Grid - 4 columns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {blog.images.map((image, idx) => (
          <ImageCard
            key={idx}
            image={image}
            title={blog.title}
            index={idx}
            onClick={() => onImageClick(image, blog.title)}
          />
        ))}
      </div>
    </div>
  );
};

// Main Blog Component
export default function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxTitle, setLightboxTitle] = useState("");

  const fetchBlogs = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/news/get-all`
      );

      if (response.data.success && Array.isArray(response.data.data)) {
        setBlogs(response.data.data);
      } else if (Array.isArray(response.data)) {
        setBlogs(response.data);
      } else if (response.data.news && Array.isArray(response.data.news)) {
        setBlogs(response.data.news);
      } else {
        throw new Error("Invalid data format received from server");
      }
    } catch (err) {
      console.error("Error fetching blogs:", err);

      if (err.response) {
        setError(
          `Server Error: ${err.response.status} - ${
            err.response.data.message || "Something went wrong"
          }`
        );
      } else if (err.request) {
        setError(
          "Network Error: Unable to connect to server. Please check your connection."
        );
      } else {
        setError(
          err.message || "Failed to fetch content. Please try again later."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleImageClick = (image, title) => {
    setLightboxImage(image);
    setLightboxTitle(title);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    setLightboxTitle("");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <Header
        breadcrumbs={[
          { label: "HOME", path: "/" },

          { label: "BLOGS", path: "/blog" },
        ]}
        title="Blog"
        description="Our blog shares insights, tips, and stories to inspire your learning journey. Discover expert advice, success stories, and the latest trends in education and skill development. Stay informed and motivated as you explore new ways to grow personally and professionally."
      />

      {/* Main Content Section */}
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-12 py-16">
        <div className="w-full mx-auto">
          {loading ? (
            // Loading State
            <div>
              {Array.from({ length: 2 }).map((_, blogIdx) => (
                <div key={blogIdx} className="mb-16">
                  <div className="h-10 bg-gray-300 rounded w-1/3 mb-8 animate-pulse"></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {Array.from({ length: 4 }).map((_, idx) => (
                      <ImageCardSkeleton key={idx} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            // Error State
            <ErrorMessage message={error} onRetry={fetchBlogs} />
          ) : blogs.length === 0 ? (
            // Empty State
            <EmptyState />
          ) : (
            // Blog Sections
            blogs.map((blog) => (
              <BlogSection
                key={blog.id || blog._id}
                blog={blog}
                onImageClick={handleImageClick}
              />
            ))
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <Lightbox
          image={lightboxImage}
          title={lightboxTitle}
          onClose={closeLightbox}
        />
      )}
    </div>
  );
}
